import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box, Flex, IconButton, useDisclosure, Text, VStack, HStack, Button } from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { Link, useLocation, useNavigate } from 'react-router';
import { theme as mythboundTheme } from '../../config/theme';
import { RoleType } from '../../config/roles';
import { useAuth } from '../../context/AuthContext';
import { GlobalFooter } from './GlobalFooter';
import LegalAcceptanceModal from '../legal/LegalAcceptanceModal';
import { TERMS_VERSION, PRIVACY_VERSION } from '../../config/legal';

/**
 * Sidebar Navigation Component
 * 
 * Implements the "wizard's spellbook concept" sidebar navigation as specified in the UI/UX requirements.
 * Provides responsive behavior with collapsible functionality for tablet and mobile views.
 */
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItemProps {
  icon: React.ReactNode;
  children: React.ReactNode;
  isActive?: boolean;
  href?: string;
}

// Navigation Item Component
const NavItem: React.FC<NavItemProps> = ({ icon, children, isActive = false, href = '#' }) => {
  return (
    <Box
      as={Link}
      to={href}
      display="flex"
      alignItems="center"
      py={3}
      px={4}
      mb={2}
      borderRadius="md"
      role="group"
      cursor="pointer"
      fontFamily={mythboundTheme.typography.fontFamily.heading}
      fontWeight={isActive ? 'bold' : 'normal'}
      color={isActive ? mythboundTheme.colors.primary.main : mythboundTheme.colors.secondary.white}
      bg={isActive ? 'rgba(59, 232, 255, 0.1)' : 'transparent'}
      _hover={{
        bg: 'rgba(59, 232, 255, 0.1)',
        color: mythboundTheme.colors.primary.main,
        boxShadow: mythboundTheme.shadows.glow,
      }}
      transition="all 0.3s"
    >
      <Box mr={4} color={isActive ? mythboundTheme.colors.primary.main : mythboundTheme.colors.secondary.white}>
        {icon}
      </Box>
      {children}
    </Box>
  );
};

// Sidebar Component
export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo, isAuthenticated, logout } = useAuth();
  const isAdmin = userInfo?.roles?.includes(RoleType.ADMIN) || false;
  
  // Function to handle profile navigation
  const handleProfileClick = () => {
    navigate('/profile');
  };

  const navItems = [
    { name: 'Dashboard', icon: '', href: '/dashboard', showAlways: true },
    { name: 'Campaigns', icon: '', href: '/campaigns', showAlways: true },
    { name: 'Characters', icon: '', href: '/characters', showAlways: true },
    { name: 'Sessions', icon: '', href: '/sessions', showAlways: true },
    { name: 'Voice Chat', icon: '', href: '/voice', showAlways: true },
    { name: 'Admin', icon: '', href: '/admin', showIfAdmin: true },
    { name: 'Dev Controls', icon: '⚙️', href: '/dev-controls', showIfAdmin: true },
    { name: 'Settings', icon: '', href: '/settings', showAlways: true },
  ];

  return (
    <Box
      bg={mythboundTheme.colors.primary.dark}
      borderRight="1px"
      borderColor={mythboundTheme.colors.accent.brown}
      w={{ base: 'full', md: 60 }}
      pos="fixed"
      h="full"
      display={{ base: isOpen ? 'block' : 'none', md: 'block' }}
      zIndex="modal"
      overflowY="auto"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: mythboundTheme.colors.accent.teal,
          borderRadius: '24px',
        },
      }}
    >
      <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
        <Box>
          <Text
            fontSize="2xl"
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            fontWeight="bold"
            color={mythboundTheme.colors.primary.gold}
          >
            Mythbound™
          </Text>
        </Box>
        <IconButton
          display={{ base: 'flex', md: 'none' }}
          onClick={onClose}
          variant="ghost"
          aria-label="close menu"
          icon={<CloseIcon />}
        />
      </Flex>

      <VStack align="stretch" spacing={2} mt={5} px={3}>
        {navItems
          .filter(item => (item.showAlways || (item.showIfAdmin && isAdmin)))
          .map((item) => (
            <NavItem 
              key={item.name} 
              icon={item.icon} 
              isActive={location.pathname === item.href} 
              href={item.href}
            >
              {item.name}
            </NavItem>
          ))}
      </VStack>

      <Box position="absolute" bottom="5" width="100%" px={3}>
        <Box
          p={3}
          borderRadius="md"
          bg="rgba(59, 232, 255, 0.05)"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.teal}
        >
          <HStack
            cursor={isAuthenticated ? "pointer" : "default"}
            onClick={isAuthenticated ? handleProfileClick : undefined}
            _hover={isAuthenticated ? {
              bg: "rgba(59, 232, 255, 0.1)",
              borderColor: mythboundTheme.colors.primary.main,
            } : {}}
            transition="all 0.2s"
            p={1}
            borderRadius="md"
          >
            <Box
              width="40px"
              height="40px"
              borderRadius="full"
              bg={mythboundTheme.colors.secondary.purple}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color={mythboundTheme.colors.secondary.white}
            >
              {userInfo?.screenName ? userInfo.screenName.charAt(0).toUpperCase() : 
               (userInfo?.username ? userInfo.username.charAt(0).toUpperCase() : '')}
            </Box>
            <VStack align="start" spacing={0}>
              <Text 
                fontWeight="medium" 
                color={mythboundTheme.colors.secondary.white}
                title={userInfo?.screenName ? (userInfo?.screenNameId ? `${userInfo.screenName}#${userInfo.screenNameId.slice(-4)}` : userInfo.screenName) : undefined}
              >
                {isAuthenticated ? (userInfo?.screenName || userInfo?.username || 'Logged In User') : 'Not Logged In'}
              </Text>
              {isAuthenticated && (
                <HStack spacing={1} fontSize="xs">
                  {/* Role Badge */}
                  <Text 
                    color={
                      userInfo?.role === 'admin' ? mythboundTheme.colors.functional.error :
                      userInfo?.role === 'gm' ? mythboundTheme.colors.primary.gold :
                      mythboundTheme.colors.accent.silver
                    }
                    fontWeight="bold"
                  >
                    {userInfo?.role?.toUpperCase() || (isAdmin ? 'ADMIN' : 'PLAYER')}
                  </Text>
                  <Text color={mythboundTheme.colors.accent.silver}>•</Text>
                  {/* Tier Badge */}
                  <Text 
                    color={
                      userInfo?.tier === 'admin' ? mythboundTheme.colors.functional.error :
                      userInfo?.tier === 'premium' ? mythboundTheme.colors.primary.gold :
                      mythboundTheme.colors.accent.silver
                    }
                  >
                    {userInfo?.tier?.toUpperCase() || 'FREE'}
                  </Text>
                  {/* Campaign Count */}
                  {userInfo?.campaign_links && userInfo.campaign_links.length > 0 && (
                    <>
                      <Text color={mythboundTheme.colors.accent.silver}>•</Text>
                      <Text color={mythboundTheme.colors.accent.teal}>
                        {userInfo.campaign_links.length} campaign{userInfo.campaign_links.length !== 1 ? 's' : ''}
                      </Text>
                    </>
                  )}
                </HStack>
              )}
              {!isAuthenticated && (
                <Text fontSize="xs" color={mythboundTheme.colors.accent.silver}>
                  Offline
                </Text>
              )}
            </VStack>
          </HStack>
          {isAuthenticated && (
            <Button 
              mt={3} 
              width="full" 
              variant="outline"
              colorScheme="red" // Keep for potential focus ring styling
              // Use functional.error for border and text color
              borderColor={mythboundTheme.colors.functional.error}
              color={mythboundTheme.colors.functional.error}
              _hover={{
                bg: 'rgba(255, 82, 82, 0.1)', // Use rgba of functional.error
                // Use functional.error for hover text color as well
                color: mythboundTheme.colors.functional.error 
              }}
              onClick={logout} // Call logout from context
            >
              Logout
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Main Layout Component that includes the Sidebar
interface LayoutProps {
  children: React.ReactNode;
  hideFooter?: boolean;
  immersiveRoutes?: string[];
}

/**
 * App-shell owner of the legal gate. AuthContext supplies the server status;
 * this component only matches those required versions to the bundled text and
 * coordinates the modal's retry, submit, and logout actions.
 */
export const Layout: React.FC<LayoutProps> = ({ children, hideFooter = false, immersiveRoutes = [] }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    userInfo, isAuthenticated, logout, legalAcceptance, legalAcceptanceStatus,
    acceptLegalAcceptance, refreshLegalAcceptance,
  } = useAuth();
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [isSubmittingLegalAcceptance, setIsSubmittingLegalAcceptance] = useState(false);
  const [legalAcceptanceError, setLegalAcceptanceError] = useState<string | null>(null);
  const legalSubmitInFlightRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isImmersiveRoute = useMemo(
    () => immersiveRoutes.some((route) => location.pathname.startsWith(route)),
    [immersiveRoutes, location.pathname]
  );

  useEffect(() => {
    if (!isAuthenticated || !userInfo?.id) {
      setShowLegalModal(false);
      setLegalAcceptanceError(null);
      return;
    }

    // Match the server's required versions against the exact text bundled here;
    // browser storage or an open connection cannot establish consent.
    const policiesMatchVisibleContent = legalAcceptance?.requiredTermsVersion === TERMS_VERSION
      && legalAcceptance?.requiredPrivacyVersion === PRIVACY_VERSION;
    const isAccepted = legalAcceptanceStatus === 'accepted' && policiesMatchVisibleContent && legalAcceptance?.accepted;
    setShowLegalModal(!isAccepted);
    if (isAccepted) setLegalAcceptanceError(null);
  }, [isAuthenticated, userInfo?.id, legalAcceptance, legalAcceptanceStatus]);

  const handleAgree = async () => {
    if (legalSubmitInFlightRef.current) return;
    if (legalAcceptanceStatus !== 'required'
      || legalAcceptance?.requiredTermsVersion !== TERMS_VERSION
      || legalAcceptance?.requiredPrivacyVersion !== PRIVACY_VERSION) return;

    legalSubmitInFlightRef.current = true;
    setIsSubmittingLegalAcceptance(true);
    setLegalAcceptanceError(null);
    try {
      // AuthContext posts the displayed versions and accepts only its server response;
      // a rejection leaves the gate open for status refresh or another attempt.
      await acceptLegalAcceptance();
    } catch (error) {
      const detail = error && typeof error === 'object' && 'detail' in error
        ? (error as { detail?: unknown }).detail
        : null;
      const nested = detail && typeof detail === 'object' && 'detail' in detail
        ? (detail as { detail?: unknown }).detail
        : detail;
      const code = nested && typeof nested === 'object' && 'code' in nested
        ? (nested as { code?: unknown }).code
        : null;
      setLegalAcceptanceError(code === 'policy_version_mismatch'
        ? 'The required policy version changed. Refresh the status and review the current policies before trying again.'
        : error instanceof Error ? error.message : 'We could not save your acceptance. Please try again.');
    } finally {
      legalSubmitInFlightRef.current = false;
      setIsSubmittingLegalAcceptance(false);
    }
  };

  const handleRetryLegalStatus = () => {
    setLegalAcceptanceError(null);
    // Refreshing status is deliberately separate from submitting consent.
    void refreshLegalAcceptance();
  };

  // Keep checking, expired-session, forbidden, service-failure, and content-version
  // mismatch states visible so none can be mistaken for permission to continue.
  const legalStatusMessage = legalAcceptanceStatus === 'pending'
    ? 'Checking your server-recorded acceptance…'
    : legalAcceptanceStatus === 'unauthenticated'
      ? 'The policy-status request rejected this session. Log out and sign in again before continuing.'
      : legalAcceptanceStatus === 'forbidden'
        ? 'The server denied access to your legal acceptance status. Sign in with an account that can access it or contact support.'
        : legalAcceptanceStatus === 'unavailable'
          ? 'We could not verify your legal acceptance. Retry when the service is available.'
          : legalAcceptanceStatus === 'update-required'
            ? 'The server requires a policy version different from the text shown here. Update the policy content before accepting.'
            : null;


  const handleDecline = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box minH="100vh" bg={mythboundTheme.colors.primary.dark}>
      <Sidebar isOpen={isOpen} onClose={onClose} />
      <Box ml={{ base: 0, md: 60 }} p={4}>
        {/* Mobile nav */}
        <Flex
          display={{ base: 'flex', md: 'none' }}
          alignItems="center"
          justifyContent="flex-start"
          mb={4}
        >
          <IconButton
            variant="ghost"
            onClick={onOpen}
            aria-label="open menu"
            icon={<HamburgerIcon />}
          />
          <Text
            ml={3}
            fontSize="xl"
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            fontWeight="bold"
            color={mythboundTheme.colors.primary.gold}
          >
            Mythbound™
          </Text>
        </Flex>

        {/* Main content */}
        <Box>{children}</Box>
        {!hideFooter && !isImmersiveRoute && <GlobalFooter hideOnRoutes={immersiveRoutes} />}
      </Box>
      <LegalAcceptanceModal
        isOpen={showLegalModal}
        onAgree={handleAgree}
        onDecline={handleDecline}
        onRetry={handleRetryLegalStatus}
        canAccept={legalAcceptanceStatus === 'required'
          && legalAcceptance?.requiredTermsVersion === TERMS_VERSION
          && legalAcceptance?.requiredPrivacyVersion === PRIVACY_VERSION}
        isSubmitting={isSubmittingLegalAcceptance}
        statusMessage={legalStatusMessage}
        errorMessage={legalAcceptanceError}
      />
    </Box>
  );
};

export default Layout;
